export interface TimeInterval {
  start_time: string;
  end_time: string;
}

export interface DaySchedule {
  is_available: boolean;
  intervals: TimeInterval[];
}

export interface WeeklyTemplate {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

export interface RoomTemplate {
  id: string;
  name: string;
  description: string;
  schedule: WeeklyTemplate;
  isCustom?: boolean;
}

// Default template configurations that can be customized
export const DEFAULT_TEMPLATE_CONFIGS = {
  'business-hours': {
    name: 'Horario Comercial',
    description: 'Lunes a Viernes, 9:00 - 18:00',
    weekdays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    startTime: '09:00',
    endTime: '18:00',
    weekendAvailable: false
  },
  'extended-hours': {
    name: 'Horario Extendido',
    description: 'Lunes a Sábado, 8:00 - 20:00',
    weekdays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
    startTime: '08:00',
    endTime: '20:00',
    weekendAvailable: false
  },
};

// Create a customizable template from configuration
export function createTemplateFromConfig(
  configKey: keyof typeof DEFAULT_TEMPLATE_CONFIGS,
  customizations?: {
    name?: string;
    description?: string;
    weekdays?: string[];
    startTime?: string;
    endTime?: string;
    secondStartTime?: string;
    secondEndTime?: string;
    weekendAvailable?: boolean;
  }
): RoomTemplate {
  const config = DEFAULT_TEMPLATE_CONFIGS[configKey];
  const custom = customizations || {};

  const weekdays = custom.weekdays || config.weekdays;
  const startTime = custom.startTime || config.startTime;
  const endTime = custom.endTime || config.endTime;
  const secondStartTime = custom.secondStartTime || (config as any).secondStartTime;
  const secondEndTime = custom.secondEndTime || (config as any).secondEndTime;

  // Create empty schedule
  const schedule: WeeklyTemplate = {
    monday: { is_available: false, intervals: [] },
    tuesday: { is_available: false, intervals: [] },
    wednesday: { is_available: false, intervals: [] },
    thursday: { is_available: false, intervals: [] },
    friday: { is_available: false, intervals: [] },
    saturday: { is_available: false, intervals: [] },
    sunday: { is_available: false, intervals: [] }
  };

  // Apply schedule to specified weekdays
  weekdays.forEach(day => {
    if (day in schedule) {
      const daySchedule = schedule[day as keyof WeeklyTemplate];
      daySchedule.is_available = true;
      
      if (secondStartTime && secondEndTime) {
        // Split shift
        daySchedule.intervals = [
          { start_time: startTime, end_time: endTime },
          { start_time: secondStartTime, end_time: secondEndTime }
        ];
      } else {
        // Single shift
        daySchedule.intervals = [
          { start_time: startTime, end_time: endTime }
        ];
      }
    }
  });

  return {
    id: configKey,
    name: custom.name || config.name,
    description: custom.description || config.description,
    schedule,
    isCustom: !!customizations
  };
}

// Generate all default templates
export function generateDefaultTemplates(): Record<string, RoomTemplate> {
  const templates: Record<string, RoomTemplate> = {};
  
  Object.keys(DEFAULT_TEMPLATE_CONFIGS).forEach(key => {
    templates[key] = createTemplateFromConfig(key as keyof typeof DEFAULT_TEMPLATE_CONFIGS);
  });
  
  return templates;
}

// Create a custom template from scratch
export function createCustomTemplate(
  name: string,
  description: string,
  schedule: WeeklyTemplate
): RoomTemplate {
  return {
    id: `custom-${Date.now()}`,
    name,
    description,
    schedule,
    isCustom: true
  };
}

// Validate time format (HH:MM)
export function isValidTimeFormat(time: string): boolean {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
}

// Validate that start time is before end time
export function isValidTimeInterval(startTime: string, endTime: string): boolean {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
    return false;
  }
  
  const start = new Date(`2000-01-01T${startTime}:00`);
  const end = new Date(`2000-01-01T${endTime}:00`);
  
  return start < end;
}

// Get available time slots for time picker (15-minute intervals)
export function getTimeSlots(): string[] {
  const slots: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeString);
    }
  }
  return slots;
}

// Calculate total weekly hours from a template
export function calculateWeeklyHours(template: RoomTemplate): number {
  let totalMinutes = 0;
  
  Object.values(template.schedule).forEach(day => {
    if (day.is_available) {
      day.intervals.forEach((interval: TimeInterval) => {
        const start = new Date(`2000-01-01T${interval.start_time}:00`);
        const end = new Date(`2000-01-01T${interval.end_time}:00`);
        totalMinutes += (end.getTime() - start.getTime()) / (1000 * 60);
      });
    }
  });
  
  return Math.round(totalMinutes / 60 * 100) / 100; // Round to 2 decimal places
}

// Clone a template with modifications
export function cloneTemplate(
  template: RoomTemplate,
  modifications?: {
    name?: string;
    description?: string;
    schedule?: Partial<WeeklyTemplate>;
  }
): RoomTemplate {
  return {
    ...template,
    id: `custom-${Date.now()}`,
    name: modifications?.name || `${template.name} (Copia)`,
    description: modifications?.description || template.description,
    schedule: modifications?.schedule ? { ...template.schedule, ...modifications.schedule } : template.schedule,
    isCustom: true
  };
}
